import { useState } from 'react'
import Formsy from 'formsy-react'

import { userSelector, billingAddressSelector, deliveryAddressSelector } from 'auth/selectors'
import { authVisible } from 'header/actions'
import featureToggles from 'shared/featureToggles'
import useRequestPending from 'shared/hooks/useRequestPending'
import useCheckoutValidation from 'shared/hooks/useCheckoutValidation'
import {
    CHECKOUT_FIELD_PAYMENT_METHOD,
    CHECKOUT_FIELD_TERMS,
    CHECKOUT_FIELD_VEHICLES,
} from 'shared/checkout-validation'

import { CardSection } from '@components/CardSection'
import ErrorMessage from '@components/ErrorMessage'
import PaymentMethodSection from '@components/PaymentMethodSection'

import {
    reviewInvoicesSelector,
    subscriptionPurchaseBuyPendingSelector,
    credentialTypeSelector,
} from 'subscription-flow/canada/selectors'
import { CREDENTIAL_DIGITAL, CREDENTIAL_LPR, DELIVERY_COLLECT, DELIVERY_HOME } from 'subscription-flow/constants'

import purchaseStyles from 'subscription-flow/styles.module.scss'

import ProrataSection from './ProrataSection'
import InvoiceSection from './InvoiceSection'
import FooterSection from './FooterSectionNew/FooterSection'
import FooterSectionOld from './FooterSectionOld/FooterSection'

import AddressSection from './AddressSection'
import VehiclesSection from './VehiclesSection'
import { ANALYTICS_FLOW_TYPE_SUBSCRIPTION } from 'analytics/constants'

const ReviewPaySelection = ({
    billingAddress,
    userDeliveryAddress,
    reviewInvoices,
    handleBuy,
    user,
    pending,
    onSuccess,
    purchaseData,
    credentialType,
}) => {
    const [paymentMethod, setPaymentMethod] = useState(null)
    const [termsAcceptedDate, setTermsAcceptedDate] = useState(null)
    const { error, isLoading } = useRequestPending(pending, () => onSuccess())
    const { delivery, startDate, vehicles } = purchaseData
    const { prorata } = reviewInvoices

    const totalToPay =
        prorata.tax + prorata.amountWithoutTax + Number(delivery?.rate?.price || delivery?.rate?.amount || 0)

    const deliveryAddress = purchaseData.deliveryAddress || userDeliveryAddress

    // The Buy button stays enabled; clicking it points out everything that is still missing.
    // The address fields are validated by Formsy itself and show their errors inline.
    const { errors, validate } = useCheckoutValidation({
        [CHECKOUT_FIELD_PAYMENT_METHOD]: !paymentMethod && <T>checkout.validation.payment.method.missing</T>,
        [CHECKOUT_FIELD_TERMS]: !termsAcceptedDate && <T>checkout.validation.terms.missing</T>,
        // VehiclesSection already displays its own message, this only blocks the purchase
        [CHECKOUT_FIELD_VEHICLES]: featureToggles.hasSubcriptionLite && vehicles.length === 0,
    })

    const onSubmit = (data = {}) => {
        if (!validate()) return

        handleBuy(paymentMethod, data.billing || billingAddress, data.delivery || deliveryAddress, termsAcceptedDate)
    }
    const hasLPR = credentialType?.indexOf(CREDENTIAL_LPR) > -1
    const hasDelivery =
        delivery &&
        delivery?.typeCode !== CREDENTIAL_DIGITAL.toUpperCase() &&
        (!featureToggles.hasSubcriptionLite || (delivery?.prdctCd !== DELIVERY_COLLECT && !hasLPR))

    return (
        <CardSection className={purchaseStyles.contentSection}>
            <Formsy noValidate onValidSubmit={onSubmit} onInvalidSubmit={validate}>
                {reviewInvoices && (
                    <>
                        <InvoiceSection invoice={reviewInvoices.nextMonth} />
                        <ProrataSection
                            delivery={delivery}
                            prorataInvoice={prorata}
                            total={totalToPay}
                            startDate={startDate}
                        />
                        {featureToggles.hasSubcriptionLite && <VehiclesSection />}
                        {hasLPR && (
                            <div className="mt-6">
                                <h3 className="font-weight-bold mb-5">
                                    <T>subscription.purchase.lpr.info.title</T>
                                </h3>
                                <p className={purchaseStyles.lprInfoContent}>
                                    <T>subscription.purchase.lpr.info.content</T>
                                </p>
                            </div>
                        )}
                        <AddressSection address={billingAddress} type="billing" />
                        {hasDelivery && (
                            <AddressSection
                                required={delivery?.prdctCd === DELIVERY_HOME || false}
                                address={deliveryAddress}
                                type="delivery"
                            />
                        )}
                    </>
                )}
                <PaymentMethodSection
                    selectedPaymentMethod={paymentMethod}
                    setPaymentMethod={setPaymentMethod}
                    paymentMethods={user?.creditCards}
                    analyticsFlowType={ANALYTICS_FLOW_TYPE_SUBSCRIPTION}
                    error={errors[CHECKOUT_FIELD_PAYMENT_METHOD]}
                />
                {featureToggles.hasTermAndConditionsNewFlow ? (
                    <FooterSection
                        loading={isLoading}
                        disabled={isLoading}
                        total={totalToPay}
                        termsAcceptedDate={termsAcceptedDate}
                        setTermsAcceptedDate={setTermsAcceptedDate}
                        termsError={errors[CHECKOUT_FIELD_TERMS]}
                    />
                ) : (
                    <FooterSectionOld
                        loading={isLoading}
                        disabled={isLoading}
                        total={totalToPay}
                        termsAcceptedDate={termsAcceptedDate}
                        setTermsAcceptedDate={setTermsAcceptedDate}
                        termsError={errors[CHECKOUT_FIELD_TERMS]}
                    />
                )}
            </Formsy>
            {error && <ErrorMessage className="mt-5" messages={[error]} />}
        </CardSection>
    )
}

export default compose(
    connect(
        createStructuredSelector({
            billingAddress: billingAddressSelector,
            userDeliveryAddress: deliveryAddressSelector,
            user: userSelector,
            reviewInvoices: reviewInvoicesSelector,
            pending: subscriptionPurchaseBuyPendingSelector,
            credentialType: credentialTypeSelector,
        }),
        { authVisible }
    )
)(ReviewPaySelection)
